import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Home as HomeIcon, IndianRupee, Users, Calendar, ShieldCheck, ImagePlus, Share2, Pencil, FileText, RotateCcw, AlertTriangle, MapPin, Trash2, PowerOff, Power, Rocket, Loader2, Sparkles } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import OwnerEarnings from "@/components/OwnerEarnings";
import OwnerIdentity from "@/components/OwnerIdentity";
import OwnerBank from "@/components/OwnerBank";
import OwnerWithdraw from "@/components/OwnerWithdraw";
import HostelImages from "@/components/HostelImages";
import HostelLocationEditor from "@/components/HostelLocationEditor";
import BoostListingDialog from "@/components/BoostListingDialog";
import { imgUrl } from "@/lib/api";
import ShareModal from "@/components/ShareModal";
import OwnerProfileEdit from "@/components/OwnerProfileEdit";
import HostelEditDialog from "@/components/HostelEditDialog";
import OwnerVisits from "@/components/OwnerVisits";

export default function OwnerDashboard() {
  const [hostels, setHostels] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [photoHostel, setPhotoHostel] = useState(null);
  const [shareHostel, setShareHostel] = useState(null);
  const [editHostel, setEditHostel] = useState(null);
  const [locationHostel, setLocationHostel] = useState(null);
  const [boostHostel, setBoostHostel] = useState(null);
  const [bookingFilter, setBookingFilter] = useState("all");
  const [form, setForm] = useState({
    name: "", city: "", address: "", coaching_centers: "",
    gender: "coed", price_per_bed: 300, total_beds: 20,
    amenities: "WiFi, Mess, Study Room", description: "", owner_phone: "",
    property_document_type: "ownership", authority_confirmed: false,
    meal_available: false, meal_price: 120, meal_description: "Home-cooked veg thali (3 meals)",
    ac_available: false, ac_price_per_bed: 420,
    room_types: [],
  });

  const load = () => {
    api.get("/owner/hostels").then((r) => setHostels(r.data));
    api.get("/owner/bookings").then((r) => setBookings(r.data));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/hostels", {
        ...form,
        price_per_bed: +form.price_per_bed,
        total_beds: +form.total_beds,
        meal_price: +form.meal_price,
        ac_price_per_bed: +form.ac_price_per_bed,
        coaching_centers: form.coaching_centers.split(",").map((s) => s.trim()).filter(Boolean),
        amenities: form.amenities.split(",").map((s) => s.trim()).filter(Boolean),
        room_types: (form.room_types || [])
          .filter((rt) => rt.name && rt.name.trim())
          .map((rt) => ({
            name: rt.name.trim(),
            price_per_month: +rt.price_per_month || 0,
            rooms_total: +rt.rooms_total || 1,
            rooms_available: +rt.rooms_available || +rt.rooms_total || 1,
            beds_per_room: +rt.beds_per_room || 1,
            description: (rt.description || "").trim(),
            food_included: !!rt.food_included,
          })),
      });
      toast.success("Hostel submitted for review!");
      setOpen(false);
      setForm({ ...form, name: "", address: "", description: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add hostel");
    }
  };

  const revenue = bookings.filter((b) => b.payment_status === "paid").reduce((s, b) => s + b.amount, 0);

  const [resubmitting, setResubmitting] = useState(null);
  const handleResubmit = async (h) => {
    setResubmitting(h.id);
    try {
      await api.post(`/owner/hostels/${h.id}/resubmit`);
      toast.success(`"${h.name}" resubmitted — admin will re-review within 24 hours.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resubmit failed");
    } finally {
      setResubmitting(null);
    }
  };

  const [togglingAvail, setTogglingAvail] = useState(null);
  const toggleAvailability = async (h) => {
    setTogglingAvail(h.id);
    try {
      const newValue = h.available === false ? true : false;
      await api.post(`/owner/hostels/${h.id}/availability`, { available: newValue });
      toast.success(newValue
        ? `"${h.name}" is now accepting bookings.`
        : `"${h.name}" is hidden from students. Bookings are paused.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update availability");
    } finally {
      setTogglingAvail(null);
    }
  };

  const [deleting, setDeleting] = useState(null);
  const deleteHostel = async (h) => {
    if (!window.confirm(`Remove "${h.name}" from the platform?\n\nThis will hide the listing from students. Existing bookings are preserved and refunds are unaffected. Admins can restore it later if you change your mind.`)) return;
    setDeleting(h.id);
    try {
      await api.delete(`/hostels/${h.id}`);
      toast.success(`"${h.name}" removed from the platform.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove listing");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF5EA]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap justify-between gap-4 items-end">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600">Owner Dashboard</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-1">Your hostels</h1>
            <a href="/verify" target="_blank" rel="noopener noreferrer" data-testid="verify-cta-link" className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 hover:bg-emerald-100 transition-colors">
              <ShieldCheck size={12} /> Verify a guest's booking code at check-in →
            </a>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <div className="flex items-center gap-3 flex-wrap">
              <a
                data-testid="download-declaration-btn"
                href={`${process.env.REACT_APP_BACKEND_URL}/api/owner/declaration.pdf?auth=${encodeURIComponent(localStorage.getItem("hn_token") || "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full border border-marigold-300 bg-marigold-50 hover:bg-marigold-100 text-vermilion-700 gap-2 h-11 px-4 text-sm font-semibold inline-flex items-center"
                title="Download your signed Property Owner Declaration as PDF"
              >
                <FileText size={15} /> Declaration PDF
              </a>
              <DialogTrigger asChild>
                <Button data-testid="add-hostel-btn" className="rounded-full bg-orange-800 hover:bg-orange-800 text-white gap-2 h-12 px-6"><Plus size={16} /> Add hostel</Button>
              </DialogTrigger>
            </div>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-2xl">List a new hostel</DialogTitle></DialogHeader>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 flex gap-2 items-start">
                <ImagePlus size={16} className="mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Photos are added after submission.</strong> First fill in the details below and submit. Once your hostel appears in "My listings", click the <em>"Photos"</em> button next to it to upload images.
                </div>
              </div>
              <form onSubmit={submit} className="grid gap-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Hostel name</Label><Input data-testid="hostel-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div><Label>City</Label><Input data-testid="hostel-city" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Kota" /></div>
                </div>
                <div><Label>Address</Label><Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div><Label>Coaching centers (comma separated)</Label><Input value={form.coaching_centers} onChange={(e) => setForm({ ...form, coaching_centers: e.target.value })} placeholder="Allen, Aakash" /></div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label>Gender</Label>
                    <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="boys">Boys</SelectItem>
                        <SelectItem value="girls">Girls</SelectItem>
                        <SelectItem value="coed">Co-ed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Price / bed / night ₹</Label><Input data-testid="hostel-price" type="number" required value={form.price_per_bed} onChange={(e) => setForm({ ...form, price_per_bed: e.target.value })} /></div>
                  <div><Label>Total beds</Label><Input type="number" required value={form.total_beds} onChange={(e) => setForm({ ...form, total_beds: e.target.value })} /></div>
                </div>
                <div><Label>Amenities (comma separated)</Label><Input value={form.amenities} onChange={(e) => setForm({ ...form, amenities: e.target.value })} /></div>
                <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div><Label>Owner phone</Label><Input value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} /></div>

                <div className="border border-slate-200 rounded-xl p-4 space-y-3">
                  <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-600">Room & meal options</div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      data-testid="hostel-ac-checkbox"
                      checked={form.ac_available}
                      onCheckedChange={(v) => setForm({ ...form, ac_available: !!v })}
                    />
                    <span className="text-sm font-semibold">Offer A/C rooms (premium)</span>
                  </label>
                  {form.ac_available && (
                    <div>
                      <Label className="text-xs">A/C price per bed / night (must be higher than ₹{form.price_per_bed})</Label>
                      <Input data-testid="hostel-ac-price" type="number" min={+form.price_per_bed + 1} value={form.ac_price_per_bed} onChange={(e) => setForm({ ...form, ac_price_per_bed: e.target.value })} className="mt-1" />
                    </div>
                  )}
                  <label className="flex items-center gap-3 cursor-pointer pt-2 border-t border-dashed border-slate-200">
                    <Checkbox
                      data-testid="hostel-meal-checkbox"
                      checked={form.meal_available}
                      onCheckedChange={(v) => setForm({ ...form, meal_available: !!v })}
                    />
                    <span className="text-sm font-semibold">Offer meals (₹100 – ₹250 / night)</span>
                  </label>
                  {form.meal_available && (
                    <>
                      <div>
                        <Label className="text-xs">Meal price per bed / night (₹100 – ₹250)</Label>
                        <Input data-testid="hostel-meal-price" type="number" min={100} max={250} value={form.meal_price} onChange={(e) => setForm({ ...form, meal_price: e.target.value })} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">Meal description</Label>
                        <Input value={form.meal_description} onChange={(e) => setForm({ ...form, meal_description: e.target.value })} placeholder="e.g. Home-cooked veg thali, 3 meals a day" className="mt-1" />
                      </div>
                    </>
                  )}
                </div>

                <div className="border border-marigold-200 bg-marigold-50/60 rounded-xl p-4 space-y-3" data-testid="hostel-room-types-section">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-xs tracking-[0.2em] uppercase font-bold text-vermilion-700">Room types (optional)</div>
                      <div className="text-[11px] text-slate-600 mt-1">Add monthly-priced options like <b>1 Seater</b>, <b>2 Seater</b>, <b>Dormitory</b>. Students pick one at booking.</div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid="add-room-type-btn"
                      onClick={() => setForm({
                        ...form,
                        room_types: [...(form.room_types || []), { name: "", price_per_month: 6000, rooms_total: 5, rooms_available: 5, beds_per_room: 1, description: "", food_included: false }],
                      })}
                      className="rounded-full gap-1 h-8 px-3"
                    >
                      <Plus size={12} /> Add room type
                    </Button>
                  </div>
                  {(form.room_types || []).map((rt, i) => (
                    <div key={i} data-testid={`room-type-row-${i}`} className="bg-white border border-slate-200 rounded-xl p-3 grid gap-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px] tracking-widest uppercase font-bold text-slate-600">Name</Label>
                          <Input data-testid={`room-type-name-${i}`} placeholder="1 Seater / Dormitory" value={rt.name} onChange={(e) => {
                            const next = [...form.room_types]; next[i] = { ...next[i], name: e.target.value }; setForm({ ...form, room_types: next });
                          }} className="mt-1 h-9" />
                        </div>
                        <div>
                          <Label className="text-[10px] tracking-widest uppercase font-bold text-slate-600">Price / month ₹</Label>
                          <Input data-testid={`room-type-price-${i}`} type="number" min={0} value={rt.price_per_month} onChange={(e) => {
                            const next = [...form.room_types]; next[i] = { ...next[i], price_per_month: e.target.value }; setForm({ ...form, room_types: next });
                          }} className="mt-1 h-9" />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-[10px] tracking-widest uppercase font-bold text-slate-600">Rooms total</Label>
                          <Input data-testid={`room-type-total-${i}`} type="number" min={1} value={rt.rooms_total} onChange={(e) => {
                            const next = [...form.room_types]; const total = e.target.value; next[i] = { ...next[i], rooms_total: total, rooms_available: Math.min(+next[i].rooms_available || 0, +total || 0) }; setForm({ ...form, room_types: next });
                          }} className="mt-1 h-9" />
                        </div>
                        <div>
                          <Label className="text-[10px] tracking-widest uppercase font-bold text-slate-600">Available</Label>
                          <Input data-testid={`room-type-available-${i}`} type="number" min={0} max={+rt.rooms_total || undefined} value={rt.rooms_available} onChange={(e) => {
                            const next = [...form.room_types]; next[i] = { ...next[i], rooms_available: e.target.value }; setForm({ ...form, room_types: next });
                          }} className="mt-1 h-9" />
                        </div>
                        <div>
                          <Label className="text-[10px] tracking-widest uppercase font-bold text-slate-600">Beds / room</Label>
                          <Input data-testid={`room-type-beds-${i}`} type="number" min={1} value={rt.beds_per_room} onChange={(e) => {
                            const next = [...form.room_types]; next[i] = { ...next[i], beds_per_room: e.target.value }; setForm({ ...form, room_types: next });
                          }} className="mt-1 h-9" />
                        </div>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700 bg-emerald-50/60 border border-emerald-200 rounded-lg px-2.5 py-2">
                        <Checkbox data-testid={`room-type-food-${i}`} checked={!!rt.food_included} onCheckedChange={(val) => {
                          const next = [...form.room_types]; next[i] = { ...next[i], food_included: !!val }; setForm({ ...form, room_types: next });
                        }} />
                        Price includes monthly food <span className="text-[10px] text-slate-500 font-normal">— students won't be charged for meals</span>
                      </label>
                      <div className="flex items-center justify-end">
                        <button type="button" data-testid={`room-type-remove-${i}`} onClick={() => {
                          const next = form.room_types.filter((_, idx) => idx !== i); setForm({ ...form, room_types: next });
                        }} className="text-xs font-semibold text-rose-700 hover:text-rose-900 inline-flex items-center gap-1">
                          <Trash2 size={12} /> Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border border-orange-100 bg-orange-50/60 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-orange-800">
                    <ShieldCheck size={14} /> Property authority
                  </div>
                  <div>
                    <Label className="text-xs">I have this property under</Label>
                    <Select value={form.property_document_type} onValueChange={(v) => setForm({ ...form, property_document_type: v })}>
                      <SelectTrigger data-testid="hostel-doc-type" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ownership">Ownership (I own it)</SelectItem>
                        <SelectItem value="lease">Registered lease agreement</SelectItem>
                        <SelectItem value="poa">Power of Attorney</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <label className="flex items-start gap-3 text-sm cursor-pointer">
                    <Checkbox
                      data-testid="hostel-authority-checkbox"
                      checked={form.authority_confirmed}
                      onCheckedChange={(v) => setForm({ ...form, authority_confirmed: !!v })}
                      className="mt-0.5"
                    />
                    <span className="text-slate-700 leading-snug">
                      I confirm I have lawful authority to lease this property and I agree to HostelNow's terms.
                      <span className="font-semibold"> Zero platform fees</span> — students pay exactly your listed price.
                    </span>
                  </label>
                </div>

                <Button data-testid="hostel-submit" type="submit" disabled={!form.authority_confirmed} className="rounded-full bg-orange-800 hover:bg-orange-800 text-white h-11 mt-2 disabled:opacity-50">Submit for approval</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: HomeIcon, label: "Hostels", value: hostels.length, cls: "bg-orange-100 text-orange-800" },
            { icon: Calendar, label: "Bookings", value: bookings.length, cls: "bg-amber-100 text-amber-800" },
            { icon: Users, label: "Total beds", value: hostels.reduce((s, h) => s + (h.total_beds || 0), 0), cls: "bg-emerald-100 text-emerald-700" },
            { icon: IndianRupee, label: "Revenue (gross)", value: `₹${revenue.toLocaleString("en-IN")}`, cls: "bg-stone-900 text-white" },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className={`w-10 h-10 rounded-xl grid place-items-center ${s.cls}`}><s.icon size={18} /></div>
              <div className="mt-4 text-xs tracking-widest uppercase font-bold text-slate-500">{s.label}</div>
              <div className="font-display font-black text-2xl mt-1">{s.value}</div>
            </div>
          ))}
        </div>

        <div className="mt-10">
          <OwnerEarnings />
        </div>

        <OwnerIdentity />

        <OwnerProfileEdit />

        <OwnerBank />

        <section className="mt-10 bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-6">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-700">How payouts work</div>
          <h2 className="font-display font-black text-2xl tracking-tight mt-1">Your money, on your terms.</h2>
          <ol className="mt-4 space-y-3 text-sm text-slate-700">
            <li className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-orange-800 text-white grid place-items-center text-xs font-black flex-shrink-0">1</div>
              <div><strong>Student pays via Razorpay.</strong> Money lands securely in HostelNow's Razorpay account.</div>
            </li>
            <li className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-orange-800 text-white grid place-items-center text-xs font-black flex-shrink-0">2</div>
              <div><strong>Your balance grows.</strong> Every paid booking's full revenue shows up in your <em>Available balance</em> below — zero platform fees, you keep 100% of room + meals.</div>
            </li>
            <li className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-orange-800 text-white grid place-items-center text-xs font-black flex-shrink-0">3</div>
              <div><strong>Request a payout anytime.</strong> Click <em>Request payout</em> — the request goes to HostelNow admin along with your verified bank/UPI details.</div>
            </li>
            <li className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-orange-800 text-white grid place-items-center text-xs font-black flex-shrink-0">4</div>
              <div><strong>Admin transfers to your bank/UPI</strong> via IMPS and marks it paid with a <em>UTR reference</em>. Typical turnaround: within 24 hours on business days.</div>
            </li>
            <li className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-orange-800 text-white grid place-items-center text-xs font-black flex-shrink-0">5</div>
              <div><strong>You see the UTR in your history.</strong> Match it with your bank statement — done.</div>
            </li>
          </ol>
          <div className="mt-4 text-[11px] text-slate-500">
            Complete your KYC and bank verification above to enable payouts. Bank details are used only for transferring your money.
          </div>
        </section>

        <OwnerWithdraw />

        <div className="mt-4 grid lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-xl">My listings</h2>
              <div className="text-[11px] text-slate-500 hidden sm:block">Tap the thumbnail to add / manage photos</div>
            </div>

            {/* Room-types nudge — shown whenever any listing has no room_types yet */}
            {(() => {
              const missing = (hostels || []).filter((h) => !Array.isArray(h.room_types) || h.room_types.length === 0);
              if (missing.length === 0) return null;
              return (
                <div
                  data-testid="room-types-nudge-banner"
                  className="mb-4 rounded-2xl border-2 border-vermilion-300 bg-gradient-to-br from-marigold-50 to-vermilion-50 p-4 sm:p-5"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-vermilion-600 text-white grid place-items-center flex-shrink-0">
                      <Sparkles size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-base sm:text-lg text-vermilion-800">
                        Add room types to {missing.length === 1 ? "your listing" : `${missing.length} of your listings`}
                      </div>
                      <div className="text-sm text-slate-700 mt-1">
                        Students now pick a room type (<b>1 Seater</b>, <b>2 Seater</b>, <b>3 Seater</b>, <b>Dormitory</b>) at booking — each with its own monthly price and availability. Listings without room types miss out on higher-value monthly bookings.
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {missing.slice(0, 4).map((h) => (
                          <button
                            key={h.id}
                            type="button"
                            data-testid={`nudge-edit-${h.id}`}
                            onClick={() => setEditHostel(h)}
                            className="inline-flex items-center gap-1.5 rounded-full bg-vermilion-600 hover:bg-vermilion-700 text-white h-9 px-3 text-xs font-bold shadow active:scale-95 transition-transform"
                          >
                            <Pencil size={12} /> Update "{h.name}"
                          </button>
                        ))}
                        {missing.length > 4 && (
                          <span className="text-xs text-slate-500 self-center">+ {missing.length - 4} more below</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="space-y-4">
              {hostels.length === 0 && <div className="text-sm text-slate-500">No hostels yet. Click "Add hostel" to list one.</div>}
              {hostels.map((h) => {
                const photoCount = (h.images || []).length;
                const cover = h.images?.[0];
                const missingRoomTypes = !Array.isArray(h.room_types) || h.room_types.length === 0;
                return (
                  <div key={h.id} className="border border-slate-200 rounded-2xl p-3 sm:p-4 flex flex-col sm:flex-row gap-4">
                    {/* Photo thumbnail — big, obvious, clickable */}
                    <button
                      type="button"
                      data-testid={`manage-photos-${h.id}`}
                      onClick={() => setPhotoHostel(h)}
                      className={`relative shrink-0 w-full sm:w-40 h-32 sm:h-28 rounded-xl overflow-hidden border-2 transition-all group ${
                        photoCount === 0
                          ? "border-dashed border-orange-400 bg-orange-50 hover:border-orange-600 hover:bg-orange-100"
                          : "border-slate-200 hover:border-orange-500"
                      }`}
                      title={photoCount === 0 ? "Add photos" : "Manage photos"}
                    >
                      {cover ? (
                        <>
                          <img src={imgUrl(cover)} alt={h.name} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <div className="text-white text-xs font-bold flex items-center gap-1.5 bg-orange-800 rounded-full px-3 py-1.5">
                              <ImagePlus size={14} /> Manage
                            </div>
                          </div>
                          <div className="absolute bottom-1.5 left-1.5 bg-white/95 backdrop-blur text-[10px] font-bold px-2 py-0.5 rounded-full text-orange-800 flex items-center gap-1">
                            <ImagePlus size={10} /> {photoCount} photo{photoCount > 1 ? "s" : ""}
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-orange-800">
                          <div className="w-10 h-10 rounded-full bg-orange-800 text-white grid place-items-center">
                            <ImagePlus size={18} />
                          </div>
                          <div className="text-xs font-bold">Add photos</div>
                          <div className="text-[10px] text-orange-800/70">Click here to upload</div>
                        </div>
                      )}
                    </button>

                    {/* Details + actions */}
                    <div className="flex-1 min-w-0 flex flex-col justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center flex-wrap gap-2">
                          <div className="font-display font-bold text-lg truncate">{h.name}</div>
                          <Badge className={h.status === "approved" ? "bg-emerald-100 text-emerald-700" : h.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}>{h.status}</Badge>
                          {h.available === false && !h.deleted_at && (
                            <Badge data-testid={`badge-unavailable-${h.id}`} className="bg-slate-800 text-white">Unavailable</Badge>
                          )}
                          {h.deleted_at && (
                            <Badge data-testid={`badge-deleted-${h.id}`} className="bg-rose-800 text-white">Removed</Badge>
                          )}
                          {h.featured_until && new Date(h.featured_until) > new Date() && (
                            <Badge data-testid={`badge-featured-${h.id}`} className="bg-turmeric-400 text-jodhpur-900 gap-1">
                              <Rocket size={11} /> Featured · until {new Date(h.featured_until).toLocaleDateString("en-IN")}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">{h.city} · ₹{h.price_per_bed}/night · {h.available_beds}/{h.total_beds} beds</div>
                        {missingRoomTypes && (
                          <button
                            type="button"
                            data-testid={`row-missing-room-types-${h.id}`}
                            onClick={() => setEditHostel(h)}
                            className="mt-2 text-[11px] text-vermilion-800 bg-marigold-50 border border-marigold-300 rounded-md px-2 py-1 inline-flex items-center gap-1 hover:bg-marigold-100"
                          >
                            <Sparkles size={11} /> Add 1/2/3-seater & dormitory room types →
                          </button>
                        )}
                        {photoCount === 0 && (
                          <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2 py-1 inline-block">
                            ⚠︎ No photos yet — students won't book without them. Tap the orange box to upload.
                          </div>
                        )}
                        {h.status === "rejected" && (
                          <div
                            data-testid={`rejection-banner-${h.id}`}
                            className="mt-2 rounded-lg bg-rose-50 border border-rose-300 px-3 py-2 flex gap-2 items-start"
                          >
                            <AlertTriangle size={14} className="text-rose-700 mt-0.5 flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="text-[11px] font-black uppercase tracking-widest text-rose-700">Rejected by admin</div>
                              <div className="text-xs text-rose-900 mt-0.5 whitespace-pre-wrap break-words">
                                {h.rejection_reason || "No reason provided — please contact support."}
                              </div>
                              <div className="text-[11px] text-rose-700/80 mt-1">
                                Fix the issue above, then click <b>Resubmit for approval</b> — we usually re-review within 24 hours.
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {h.status === "rejected" && (
                          <Button
                            data-testid={`resubmit-hostel-${h.id}`}
                            size="sm"
                            onClick={() => handleResubmit(h)}
                            disabled={resubmitting === h.id}
                            className="rounded-full gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            <RotateCcw size={14} className={resubmitting === h.id ? "animate-spin" : ""} />
                            {resubmitting === h.id ? "Resubmitting…" : "Resubmit for approval"}
                          </Button>
                        )}
                        <Button
                          data-testid={`add-photos-cta-${h.id}`}
                          size="sm"
                          onClick={() => setPhotoHostel(h)}
                          className="rounded-full gap-1 bg-orange-800 hover:bg-orange-900 text-white"
                        >
                          <ImagePlus size={14} /> {photoCount === 0 ? "Add photos" : "Manage photos"}
                        </Button>
                        <Button data-testid={`edit-hostel-${h.id}`} variant="outline" size="sm" onClick={() => setEditHostel(h)} className="rounded-full gap-1">
                          <Pencil size={14} /> Edit details
                        </Button>
                        <Button
                          data-testid={`set-location-${h.id}`}
                          variant="outline"
                          size="sm"
                          onClick={() => setLocationHostel(h)}
                          className={`rounded-full gap-1 ${h.map_address ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}`}
                          title={h.map_address ? `Saved: ${h.map_address}` : "No exact map pin set yet — students see only city until you set one."}
                        >
                          <MapPin size={14} /> {h.map_address ? "Update location" : "Set location on map"}
                        </Button>
                        <Button data-testid={`share-hostel-${h.id}`} variant="outline" size="sm" onClick={() => setShareHostel(h)} className="rounded-full gap-1">
                          <Share2 size={14} /> Share
                        </Button>
                        {h.status === "approved" && !h.deleted_at && (
                          <Button
                            data-testid={`boost-hostel-${h.id}`}
                            size="sm"
                            onClick={() => setBoostHostel(h)}
                            className="rounded-full gap-1 bg-turmeric-400 hover:bg-turmeric-500 text-jodhpur-900"
                          >
                            <Rocket size={14} /> {h.featured_until && new Date(h.featured_until) > new Date() ? "Extend boost" : "Boost listing"}
                          </Button>
                        )}
                        {!h.deleted_at && (
                          <Button
                            data-testid={`toggle-avail-${h.id}`}
                            variant="outline"
                            size="sm"
                            onClick={() => toggleAvailability(h)}
                            disabled={togglingAvail === h.id}
                            className={`rounded-full gap-1 ${h.available === false ? "border-emerald-300 text-emerald-700" : "border-slate-300 text-slate-700"}`}
                            title={h.available === false ? "Currently hidden from students — click to make bookable again" : "Currently accepting bookings — click to pause"}
                          >
                            {togglingAvail === h.id ? <Loader2 size={14} className="animate-spin" /> : h.available === false ? <Power size={14} /> : <PowerOff size={14} />}
                            {h.available === false ? "Set available" : "Set unavailable"}
                          </Button>
                        )}
                        {!h.deleted_at && (
                          <Button
                            data-testid={`delete-hostel-${h.id}`}
                            variant="outline"
                            size="sm"
                            onClick={() => deleteHostel(h)}
                            disabled={deleting === h.id}
                            className="rounded-full gap-1 border-rose-300 text-rose-700 hover:bg-rose-50"
                          >
                            {deleting === h.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
              <h2 className="font-display font-bold text-xl">Recent bookings</h2>
              {hostels.length > 1 && (
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="owner-bookings-filter">
                  <button
                    data-testid="owner-booking-filter-all"
                    onClick={() => setBookingFilter("all")}
                    className={`text-[11px] font-bold rounded-full px-3 py-1 border ${bookingFilter === "all" ? "bg-orange-800 text-white border-orange-800" : "bg-white text-slate-600 border-slate-200 hover:border-orange-300"}`}
                  >
                    All ({bookings.length})
                  </button>
                  {hostels.map((h) => {
                    const count = bookings.filter((b) => b.hostel_id === h.id).length;
                    if (count === 0) return null;
                    return (
                      <button
                        key={h.id}
                        data-testid={`owner-booking-filter-${h.id}`}
                        onClick={() => setBookingFilter(h.id)}
                        className={`text-[11px] font-bold rounded-full px-3 py-1 border truncate max-w-[180px] ${bookingFilter === h.id ? "bg-orange-800 text-white border-orange-800" : "bg-white text-slate-600 border-slate-200 hover:border-orange-300"}`}
                        title={h.name}
                      >
                        {h.name} ({count})
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="space-y-3">
              {(() => {
                const filtered = bookingFilter === "all" ? bookings : bookings.filter((b) => b.hostel_id === bookingFilter);
                if (filtered.length === 0) {
                  return (
                    <div className="text-sm text-slate-500">
                      {bookingFilter === "all" ? "No bookings yet." : "No bookings for this hostel yet."}
                    </div>
                  );
                }
                return filtered.slice(0, 12).map((b) => (
                <div key={b.id} data-testid={`owner-booking-${b.id}`} data-payment-status={b.payment_status} className={`relative rounded-xl p-4 flex items-center justify-between overflow-hidden ${b.payment_status === "paid" ? "border border-slate-200 bg-white" : "border-2 border-dashed border-rose-300 bg-rose-50/30"}`}>
                  {b.payment_status !== "paid" && (
                    <div className="absolute -top-2 -right-14 rotate-45 bg-rose-600 text-white text-[9px] font-black tracking-widest uppercase px-14 py-0.5">
                      NOT PAID
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-sm">{b.hostel_name}</div>
                    <div className="text-xs text-slate-500">{b.check_in} → {b.check_out} · {b.beds} bed(s)</div>
                    {b.payment_status !== "paid" && (
                      <div className="text-[10px] text-rose-700 font-semibold mt-1">Reservation slot only · treat as unconfirmed</div>
                    )}
                    {b.payment_status === "paid" && b.verification_code && (
                      <div className="text-[10px] font-mono text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 inline-block mt-1">
                        {b.verification_code}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className={`font-semibold ${b.payment_status === "paid" ? "text-emerald-700" : "text-rose-700"}`}>₹{b.amount}</div>
                    <Badge className={b.payment_status === "paid" ? "bg-emerald-600 text-white" : "bg-rose-100 text-rose-700"}>{b.payment_status}</Badge>
                  </div>
                </div>
              ));
              })()}
            </div>
          </div>
          <OwnerVisits />
        </div>
      </div>
      {photoHostel && (
        <HostelImages
          hostel={photoHostel}
          open={!!photoHostel}
          onClose={() => setPhotoHostel(null)}
          onChange={load}
        />
      )}
      {shareHostel && (
        <ShareModal
          hostel={shareHostel}
          open={!!shareHostel}
          onClose={() => setShareHostel(null)}
        />
      )}
      {editHostel && (
        <HostelEditDialog
          hostel={editHostel}
          open={!!editHostel}
          onClose={() => setEditHostel(null)}
          onSaved={() => load()}
        />
      )}
      {locationHostel && (
        <HostelLocationEditor
          hostel={locationHostel}
          open={!!locationHostel}
          onClose={() => setLocationHostel(null)}
          onSaved={() => load()}
        />
      )}
      {boostHostel && (
        <BoostListingDialog
          hostel={boostHostel}
          open={!!boostHostel}
          onClose={() => setBoostHostel(null)}
          onBoosted={() => load()}
        />
      )}
      <Footer />
    </div>
  );
}
